// src/pages/PrivacyPage.js
import React from 'react';
import LegalDocument from '../components/legal/LegalDocument';
import AnimatedShield from '../components/legal/AnimatedShield';
import useSEO from '../hooks/useSEO';

const SECTIONS = [
  {
    id: 'introduction',
    number: 1,
    title: 'Introduction',
    blocks: [
      {
        type: 'p',
        text: 'Dub Labs ("we," "our," or "us") provides AI-powered video dubbing and translation services through our white-label platform. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our services at dublabs.ai or any white-labeled instance of our platform.',
      },
      {
        type: 'p',
        text: 'By using our services, you agree to the collection and use of information in accordance with this policy.',
      },
    ],
  },
  {
    id: 'information-we-collect',
    number: 2,
    title: 'Information We Collect',
    blocks: [
      { type: 'h3', text: '2.1 Information You Provide' },
      {
        type: 'ul',
        items: [
          { label: 'Account Information', text: 'Email address, organization name, and role within your organization.' },
          { label: 'User Content', text: 'Videos, audio files, and any content you upload for processing.' },
          { label: 'Usage Data', text: 'Information about how you interact with our services.' },
          { label: 'Payment Information', text: 'Processed through third-party payment processors — we do not store credit card details.' },
        ],
      },
      { type: 'h3', text: '2.2 Automatically Collected Information' },
      {
        type: 'ul',
        items: [
          { label: 'Log Data', text: 'IP addresses, browser type, operating system, access times, and pages viewed.' },
          { label: 'Device Information', text: 'Hardware model, operating system version, unique device identifiers.' },
          { label: 'Analytics Data', text: 'Performance metrics, feature usage, and error reports.' },
        ],
      },
      { type: 'h3', text: '2.3 Third-Party Service Data' },
      { type: 'p', text: 'We integrate with third-party services for:' },
      {
        type: 'ul',
        items: [
          'Voice processing and translation',
          'Video synchronization',
          'Email authentication and notifications',
          'Cloud infrastructure and storage',
        ],
      },
    ],
  },
  {
    id: 'how-we-use',
    number: 3,
    title: 'How We Use Your Information',
    blocks: [
      { type: 'p', text: 'We use collected information to:' },
      {
        type: 'ul',
        items: [
          'Provide, maintain, and improve our video dubbing services',
          'Process your videos through our AI pipeline',
          'Authenticate users and manage accounts',
          'Monitor usage for billing and quota management',
          'Send service-related communications',
          'Comply with legal obligations',
          'Protect against fraudulent or illegal activity',
          'Generate anonymized analytics to improve service quality',
        ],
      },
      { type: 'h3', text: '3.1 Content Processing' },
      {
        type: 'ul',
        items: [
          'Your uploaded videos are processed solely for providing dubbing services',
          'We do not use your content to train our AI models without explicit consent',
          'Demonstration videos on our website are not derived from user uploads',
        ],
      },
    ],
  },
  {
    id: 'data-retention',
    number: 4,
    title: 'Data Retention',
    blocks: [
      {
        type: 'ul',
        items: [
          { label: 'User Content', text: 'Videos and processed content are retained for 90 days after processing, unless deleted earlier by the user.' },
          { label: 'Account Data', text: 'Retained for the duration of your subscription plus 12 months.' },
          { label: 'Usage Logs', text: 'Retained for 24 months for security and billing purposes.' },
          { label: 'Deleted Content', text: 'Permanently removed from our systems within 30 days of deletion request.' },
        ],
      },
    ],
  },
  {
    id: 'data-sharing',
    number: 5,
    title: 'Data Sharing and Disclosure',
    blocks: [
      { type: 'p', text: 'We do not sell, rent, or trade your personal information. We may share information with:' },
      { type: 'h3', text: '5.1 Service Providers' },
      {
        type: 'ul',
        items: [
          'Third-party services essential for video and audio processing',
          'Cloud infrastructure providers',
          'Email service providers',
          'Payment processors',
        ],
      },
      { type: 'h3', text: '5.2 White-Label Partners' },
      { type: 'p', text: 'For white-labeled instances, we share data with the organization administrator as necessary for service provision.' },
      { type: 'h3', text: '5.3 Legal Requirements' },
      { type: 'p', text: 'We may disclose information if required by law or in response to valid legal requests.' },
      { type: 'h3', text: '5.4 Business Transfers' },
      { type: 'p', text: 'In the event of a merger, acquisition, or sale of assets, user information may be transferred to the successor entity.' },
    ],
  },
  {
    id: 'data-security',
    number: 6,
    title: 'Data Security',
    blocks: [
      { type: 'p', text: 'We implement industry-standard security measures including:' },
      {
        type: 'ul',
        items: [
          'Encryption of data in transit and at rest',
          'Regular security audits and penetration testing',
          'Access controls and authentication mechanisms',
          'Secure data centers with physical security controls',
          'Regular backups and disaster recovery procedures',
        ],
      },
      { type: 'p', text: 'However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.' },
    ],
  },
  {
    id: 'your-rights',
    number: 7,
    title: 'Your Rights and Controls',
    blocks: [
      { type: 'p', text: 'You have the right to:' },
      {
        type: 'ul',
        items: [
          'Access your personal information',
          'Correct inaccurate data',
          'Request deletion of your data (subject to legal requirements)',
          'Export your data in a portable format',
          'Opt-out of marketing communications',
          'Request restriction of processing',
        ],
      },
      { type: 'p', text: 'To exercise these rights, contact us at hello@aiguru.in.' },
    ],
  },
  {
    id: 'international-transfers',
    number: 8,
    title: 'International Data Transfers',
    blocks: [
      { type: 'p', text: 'Our services are hosted in the United States. If you access our services from outside the US, your information will be transferred to and processed in the US. We ensure appropriate safeguards are in place for international transfers.' },
    ],
  },
  {
    id: 'cookies',
    number: 9,
    title: 'Cookies and Tracking',
    blocks: [
      { type: 'p', text: 'We use essential cookies for authentication and session management. We do not use third-party tracking cookies or advertising cookies. You can control cookie settings through your browser.' },
    ],
  },
  {
    id: 'children',
    number: 10,
    title: "Children's Privacy",
    blocks: [
      { type: 'p', text: 'Our services are not intended for users under 18 years of age. We do not knowingly collect information from children.' },
    ],
  },
  {
    id: 'california',
    number: 11,
    title: 'California Privacy Rights',
    blocks: [
      { type: 'p', text: "California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect and how it's used, the right to delete personal information, and the right to opt-out of the sale of personal information (which we do not do)." },
    ],
  },
  {
    id: 'changes',
    number: 12,
    title: 'Changes to This Policy',
    blocks: [
      { type: 'p', text: 'We may update this Privacy Policy from time to time. We will notify you of material changes via email or through the service. Continued use after changes constitutes acceptance of the updated policy.' },
    ],
  },
];

const CONTACT = {
  name: 'Dub Labs',
  email: 'hello@aiguru.in',
  address: 'Gurukul Road, Ahmedabad, Gujarat – 380052',
};

export default function PrivacyPage() {
  useSEO({
    title: 'Privacy Policy — DubLabs AI Video Dubbing Platform',
    description:
      'How DubLabs collects, uses, and protects your data. Interactive privacy policy with searchable sections, breadcrumb navigation, and copy-to-clipboard.',
    canonical: 'https://dublabs.ai/privacy',
    breadcrumbs: [
      { name: 'Legal', url: 'https://dublabs.ai/privacy' },
      { name: 'Privacy Policy', url: 'https://dublabs.ai/privacy' },
    ],
    dateModified: '2026-07-30',
  });

  return (
    <LegalDocument
      eyebrow="Legal"
      title="Privacy Policy"
      subtitle="How we collect, use, and protect the information you share with DubLabs. Search, jump, and read at your own pace."
      lastUpdated="June 2025"
      icon={<AnimatedShield size={120} />}
      sections={SECTIONS}
      contact={CONTACT}
    />
  );
}
